import POSButton from '@/components/POS/Common/POSButton';
import POSHeading from '@/components/POS/Common/POSHeading';
import POSInput from '@/components/POS/Common/POSInput';
import POSSelect from '@/components/POS/Common/POSSelect';
import POSSwitch from '@/components/POS/Common/POSSwitch';
import POSTextArea from '@/components/POS/Common/POSTextArea';
import { api } from '@/utils/Api/POS';
import { AppBar, Box, Grid2, Skeleton, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import CustomRichTextEditor, { CustomRichTextEditorHandle } from '@/components/Marketing/CustomRichTextEditor';
import generatePreviewHtml, { stripTokenEscapes } from '@/utils/PreviewHtml';
import { useSelector } from 'react-redux';
import POSTrustpilotTestModel, { ReviewChannelType } from './POSTrustpilotTestModel';
import { RadixButton } from '@/components/radix';

interface InitialValueDataType {
    enabled: boolean;
    trustPilotAFSEmail: string;
    reviewChannel: ReviewChannelType;
    reviewLink: string | null;
    emailSubject: string | null;
    emailContent: string | null;
    bccEmail: string | null;
    smsContent: string | null;
    sendDelayHours: number | undefined;
}

const optionsReviewReqestTime = [
    { value: 1, label: t('Marketing.DaySelectorDurationAmountHOURS_one', { count: 1 }) },
    { value: 24, label: t('Marketing.DaySelectorDurationAmountDAYS_one', { count: 1 }) },
    { value: 48, label: t('Marketing.DaySelectorDurationAmountDAYS_other', { count: 2 }) },
];

const optionsReviewChannel = [
    { value: 'EMAIL', label: t('Common.Email') },
    { value: 'SMS', label: t('Calendar.SMS') },
    { value: 'EMAIL_AND_SMS', label: `${t('Common.Email')} ${t('Common.AND')} ${t('Calendar.SMS')}` },
];

const TrustpilotKeywords = [
    'customer_name',
    'outlet_name',
    'service_name',
    'booking_date',
    'booking_time',
    'outlet_address',
    'review_link',
];

const POSTrustpilot = () => {
    const editorRef = useRef<CustomRichTextEditorHandle | null>(null);
    const setting = useSelector((state: any) => state?.settings?.data?.profile);
    const isInspectionEnabled = setting.inspection_module;
    const [isLoading, setLoading] = useState(false);
    const [openTestModel, setOpenTestModel] = useState(false);
    /** Default EMAIL template for TRUSTPILOT, from the communication templates API */
    const [defaultEmail, setDefaultEmail] = useState<{ email_subject: string; email_body: string }>({
        email_subject: '',
        email_body: '',
    });
    const [initialValues, setInitialValues] = useState<InitialValueDataType>({
        enabled: false,
        trustPilotAFSEmail: '',
        sendDelayHours: 1,
        reviewChannel: 'EMAIL',
        reviewLink: '',
        emailSubject: '',
        emailContent: '',
        smsContent: '',
        bccEmail: '',
    });

    // Close over isInspectionEnabled — Yup `$context` is never passed by Formik.
    // Match UI: inspection ON → AFS email; inspection OFF → review link + email/SMS content.
    const validationSchema = useMemo(
        () =>
            Yup.object({
                enabled: Yup.boolean(),
                trustPilotAFSEmail: Yup.string()
                    .nullable()
                    .when(['enabled'], {
                        is: (enabled: boolean) => enabled && isInspectionEnabled,
                        then: (schema) =>
                            schema.required(t('Setting.EmailIsRequired')).email(t('Setting.InvalidEmailFormat')),
                        otherwise: (schema) => schema.notRequired().nullable(),
                    }),

                reviewLink: isInspectionEnabled
                    ? Yup.string().notRequired().nullable()
                    : Yup.string()
                          .transform((value) => value ?? '')
                          .required(t('Integration.WebhookUrlInvalid'))
                          .url(t('Integration.WebhookUrlInvalid')),

                emailSubject: Yup.string().when(['reviewChannel'], {
                    is: (channel: ReviewChannelType) =>
                        !isInspectionEnabled && (channel === 'EMAIL' || channel === 'EMAIL_AND_SMS'),
                    then: (schema) => schema.required(t('Services.EmailHeaderReq')),
                    otherwise: (schema) => schema.notRequired().nullable(),
                }),

                emailContent: Yup.string().when(['reviewChannel'], {
                    is: (channel: ReviewChannelType) =>
                        !isInspectionEnabled && (channel === 'EMAIL' || channel === 'EMAIL_AND_SMS'),
                    then: (schema) =>
                        schema
                            .required(t('Services.EmailReq'))
                            .test('email-content-not-empty', t('Services.EmailReq'), (value) => {
                                if (!value) return false;

                                const parser = new DOMParser();
                                const doc = parser.parseFromString(value, 'text/html');

                                const bodyText = doc.body.textContent?.trim();
                                const hasImage = doc.body.querySelector('img') !== null;

                                return !!bodyText || hasImage;
                            }),
                    otherwise: (schema) => schema.notRequired().nullable(),
                }),

                bccEmail: Yup.string().nullable().email(t('Setting.InvalidEmailFormat')),

                smsContent: Yup.string().when(['reviewChannel'], {
                    is: (channel: ReviewChannelType) =>
                        !isInspectionEnabled && (channel === 'SMS' || channel === 'EMAIL_AND_SMS'),
                    then: (schema) => schema.required(t('Services.SMSReq')),
                    otherwise: (schema) => schema.notRequired().nullable(),
                }),
            }),
        [isInspectionEnabled],
    );

    const formik = useFormik<InitialValueDataType>({
        initialValues,
        enableReinitialize: true,
        validationSchema,
        onSubmit: (values) => {
            callPostApi(values);
        },
    });

    /** Default TRUSTPILOT email template; the saved config (if any) takes precedence over it */
    const fetchDefaultEmailTemplate = useCallback(async () => {
        try {
            const response = await api.postApiCommunicationTemplates({
                identifiers: ['TRUSTPILOT'],
                modes: ['EMAIL'],
            });
            const template = response?.data?.templates?.TRUSTPILOT?.EMAIL?.default;
            return {
                email_subject: template?.subject ?? '',
                email_body: template?.template ?? '',
            };
        } catch (error) {
            console.error('Failed to fetch TRUSTPILOT communication template', error);
            return { email_subject: '', email_body: '' };
        }
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [response, template] = await Promise.all([api.getApiTrustpilotConfig(), fetchDefaultEmailTemplate()]);
            setDefaultEmail(template);
            if (response) {
                const data = response?.data as InitialValueDataType;
                setInitialValues({
                    ...data,
                    emailSubject: data?.emailSubject || template.email_subject,
                    emailContent: data?.emailContent || template.email_body,
                    bccEmail: data?.bccEmail || '',
                });
            }
        } catch (error) {
            console.error('Error : ', error);
        } finally {
            setLoading(false);
        }
    }, [fetchDefaultEmailTemplate]);

    const callPostApi = async (values: InitialValueDataType) => {
        setLoading(true);
        try {
            const response = await api.postApiTrustpilot({
                enabled: values?.enabled,
                trustPilotAFSEmail: values?.trustPilotAFSEmail,
                sendDelayHours: values?.sendDelayHours,
                reviewChannel: values?.reviewChannel,
                reviewLink: values?.reviewLink,
                emailSubject: stripTokenEscapes(values?.emailSubject),
                emailContent: stripTokenEscapes(values?.emailContent),
                smsContent: stripTokenEscapes(values?.smsContent),
                bccEmail: values?.bccEmail || null,
            });

            if (response) {
                toast.success(
                    `${t('Integration.Trustpilot')} ${t('GiftCard.Settings')}  ${t('Services.ToastUpSuccess')}`,
                );
                fetchData();
            }
        } catch (error) {
            console.error('Error : ', error);
            toast.error(t('Services.ToastUpErr'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    console.log('formik.values', formik);

    return (
        <Box p={4.5}>
            {formik?.dirty && (
                <AppBar
                    sx={{
                        position: 'fixed',
                        top: 45,
                        left: 0,
                        right: 0,
                        py: 1,
                        px: 4,
                        bgcolor: '#fff',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'flex-end',
                        width: '100%',
                        zIndex: 8,
                        mb: 5,
                    }}
                >
                    <POSButton
                        variant="save"
                        width="auto"
                        height={35}
                        title={t('Setting.SaveChanges')}
                        onClick={formik.handleSubmit}
                        loading={isLoading}
                    />
                </AppBar>
            )}
            <React.Fragment>
                <Grid2
                    container
                    sx={{ p: { xs: 2, md: 5 }, background: '#fff', borderRadius: '25px', mt: formik.dirty ? 7 : 0 }}
                >
                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <POSHeading text={t('Integration.EnableTrustPilot')} />
                    </Grid2>
                    {isLoading ? (
                        <Stack sx={{ width: '65%' }}>
                            <Skeleton variant="rounded" sx={{ width: '100%' }} height={50} />
                        </Stack>
                    ) : (
                        <Grid2 size={{ xs: 12, md: 8 }}>
                            <POSSwitch
                                checked={formik.values.enabled}
                                onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    formik.setFieldValue('enabled', isChecked);
                                }}
                            />

                            <Box className={'space-y-3'} marginTop={2}>
                                {isInspectionEnabled ? (
                                    <>
                                        <Typography fontWeight={700}>
                                            {t('Integration.TrustpilotTextFieldHeader')}
                                        </Typography>
                                        <POSInput
                                            value={formik.values.trustPilotAFSEmail}
                                            onChange={(e) => formik.setFieldValue('trustPilotAFSEmail', e.target.value)}
                                            onBlur={() => formik.setFieldTouched('trustPilotAFSEmail', true)}
                                            sx={{ width: '100%' }}
                                            type="email"
                                            error={
                                                formik.touched.trustPilotAFSEmail &&
                                                Boolean(formik.errors.trustPilotAFSEmail)
                                            }
                                        />

                                        {formik.touched.trustPilotAFSEmail && formik.errors.trustPilotAFSEmail && (
                                            <Typography variant="caption" color="error">
                                                {formik.errors.trustPilotAFSEmail}
                                            </Typography>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <Typography fontWeight={700}>
                                            {t('Integration.Trustpilot')} {t('Common.Link')}
                                        </Typography>
                                        <POSInput
                                            value={formik.values.reviewLink}
                                            placeholder={`${t('Integration.Trustpilot')} ${t('Common.Link')}`}
                                            onChange={(e) => formik.setFieldValue('reviewLink', e.target.value)}
                                            onBlur={() => formik.setFieldTouched('reviewLink', true)}
                                            sx={{ width: '100%' }}
                                            error={formik.touched.reviewLink && Boolean(formik.errors.reviewLink)}
                                        />

                                        {formik.touched.reviewLink && formik.errors.reviewLink && (
                                            <Typography variant="caption" color="error">
                                                {formik.errors.reviewLink}
                                            </Typography>
                                        )}

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                alignItems: 'center',
                                            }}
                                        >
                                            <POSSelect
                                                id={'setCalendarOpeningHour'}
                                                placeholderText={t('GiftCard.SelectOption')}
                                                value={formik.values.sendDelayHours}
                                                onChange={(e) => formik.setFieldValue('sendDelayHours', e.target.value)}
                                                options={optionsReviewReqestTime}
                                            />

                                            <POSSelect
                                                id={'setCalendarOpeningHour'}
                                                placeholderText={t('GiftCard.SelectOption')}
                                                value={formik.values.reviewChannel}
                                                onChange={(e) => formik.setFieldValue('reviewChannel', e.target.value)}
                                                options={optionsReviewChannel}
                                            />
                                        </Box>

                                        {(formik.values.reviewChannel === 'EMAIL' ||
                                            formik.values.reviewChannel === 'EMAIL_AND_SMS') && (
                                            <>
                                                <Typography fontWeight={700}>
                                                    {t('Common.Email')} {t('Common.Subject')}
                                                </Typography>
                                                <POSInput
                                                    placeholder={`${t('Common.Email')} ${t('Common.Subject')}`}
                                                    value={formik.values.emailSubject}
                                                    onChange={(e) =>
                                                        formik.setFieldValue('emailSubject', e.target.value)
                                                    }
                                                    onBlur={() => formik.setFieldTouched('emailSubject', true)}
                                                    sx={{ width: '100%' }}
                                                    type="email"
                                                    error={
                                                        formik.touched.emailSubject &&
                                                        Boolean(formik.errors.emailSubject)
                                                    }
                                                />

                                                {formik.touched.emailSubject && formik.errors.emailSubject && (
                                                    <Typography variant="caption" color="error">
                                                        {formik.errors.emailSubject}
                                                    </Typography>
                                                )}

                                                <Typography fontWeight={700}>
                                                    {t('Marketing.BccEmail')} ({t('Common.Optional')})
                                                </Typography>
                                                <POSInput
                                                    placeholder={t('Marketing.BccEmail')}
                                                    value={formik.values.bccEmail ?? ''}
                                                    onChange={(e) => formik.setFieldValue('bccEmail', e.target.value)}
                                                    onBlur={() => formik.setFieldTouched('bccEmail', true)}
                                                    sx={{ width: '100%' }}
                                                    type="email"
                                                    error={formik.touched.bccEmail && Boolean(formik.errors.bccEmail)}
                                                />

                                                {formik.touched.bccEmail && formik.errors.bccEmail && (
                                                    <Typography variant="caption" color="error">
                                                        {formik.errors.bccEmail}
                                                    </Typography>
                                                )}

                                                <Typography fontWeight={700}>{t('Setting.EmailBody')}</Typography>

                                                <CustomRichTextEditor
                                                    ref={editorRef}
                                                    placeholder={`${t('Setting.EmailBody')}`}
                                                    value={generatePreviewHtml({
                                                        formik,
                                                        defaultAdvanceReminderEmail: defaultEmail,
                                                        setting,
                                                    })}
                                                    onChange={(val: string) =>
                                                        formik.setFieldValue('emailContent', val)
                                                    }
                                                    className="w-full px-0 border rounded-none rounded-b-md"
                                                />

                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {TrustpilotKeywords.map((keyword: string) => (
                                                        <RadixButton
                                                            key={keyword}
                                                            onClick={() =>
                                                                editorRef.current?.insertTextAtCursor(`\${${keyword}} `)
                                                            }
                                                            variant="outline"
                                                            size="xs"
                                                            className="!bg-[#fff4e9]  !border-none min-w-fit !py-0 !px-0 !h-auto m-0"
                                                        >
                                                            {`{${keyword}}`}
                                                        </RadixButton>
                                                    ))}
                                                </div>

                                                {formik.touched.emailContent && formik.errors.emailContent && (
                                                    <Typography variant="caption" color="error">
                                                        {formik.errors.emailContent}
                                                    </Typography>
                                                )}
                                            </>
                                        )}
                                        {(formik.values.reviewChannel === 'SMS' ||
                                            formik.values.reviewChannel === 'EMAIL_AND_SMS') && (
                                            <>
                                                <Typography fontWeight={700}>
                                                    {t('Calendar.SMS')} {t('Doctors.NotificationMessage')}
                                                </Typography>
                                                <POSTextArea
                                                    value={formik.values.smsContent ?? ''}
                                                    placeholder={`${t('Calendar.SMS')} ${t('Doctors.NotificationMessage')}`}
                                                    onChange={(e) => formik.setFieldValue('smsContent', e.target.value)}
                                                    onBlur={() => formik.setFieldTouched('smsContent', true)}
                                                    sx={{ width: '100%' }}
                                                    type="email"
                                                    error={
                                                        formik.touched.smsContent && Boolean(formik.errors.smsContent)
                                                    }
                                                />
                                            </>
                                        )}

                                        {formik.touched.smsContent && formik.errors.smsContent && (
                                            <Typography variant="caption" color="error">
                                                {formik.errors.smsContent}
                                            </Typography>
                                        )}
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                                            <POSButton
                                                variant="save"
                                                width="auto"
                                                className={'self-end'}
                                                height={35}
                                                title={`${t('Common.SendTest')}`}
                                                onClick={() => {
                                                    if (formik.dirty) {
                                                        toast.error(t('Common.PleaseSaveBeforeProceeding'), {
                                                            style: {
                                                                zIndex: 100,
                                                            },
                                                        });
                                                        return;
                                                    }
                                                    setOpenTestModel(true);
                                                }}
                                            />
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Grid2>
                    )}
                </Grid2>

                {openTestModel && (
                    <POSTrustpilotTestModel
                        openTestModel={openTestModel}
                        handleCloseModel={() => {
                            setOpenTestModel(false);
                        }}
                        reviewChannel={formik.values.reviewChannel}
                        isSaved={formik?.dirty}
                    />
                )}
            </React.Fragment>
        </Box>
    );
};

export default POSTrustpilot;
